"use client";

import React from "react";
import { Check, Loader2, Network } from "lucide-react";
import {
  ADDRESS_STATE_LABEL,
  AddressHealth,
  AddressState,
  GatewayEndpoint,
  endpointKey,
} from "@/lib/gateways";

const STATE_CLASS: Record<AddressState, string> = {
  checking: "text-muted-foreground",
  ready: "text-success",
  "not-ready": "text-muted-foreground",
  unreachable: "text-destructive",
};

/**
 * The gateway addresses of one broker server, one selectable row each: `host:port`, its live state
 * (from the address's own `/healthz`) and the measured round trip. Used by the Add Account server
 * and account steps and by Edit Account, so the trader sees and picks an address the same way in
 * all three places.
 */
export function GatewayAddressList({
  endpoints,
  health,
  selectedIndex,
  onSelect,
}: {
  endpoints: readonly GatewayEndpoint[];
  health: Readonly<Record<string, AddressHealth | undefined>>;
  selectedIndex: number;
  onSelect: (index: number) => void;
}) {
  return (
    <div className="space-y-1.5" role="radiogroup" aria-label="Gateway address">
      {endpoints.map((endpoint, index) => {
        const h = health[endpointKey(endpoint)];
        const state: AddressState = h?.state ?? "checking";
        const selected = index === selectedIndex;
        return (
          <button
            key={endpointKey(endpoint)}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onSelect(index)}
            className={`w-full text-left rounded-xl border bg-card hover:border-primary/50 hover:bg-accent/40 transition-colors p-3 flex items-center gap-3 group ${
              selected ? "border-primary/50 bg-accent/40" : "border-border"
            }`}
          >
            <div className="h-9 w-9 rounded-lg bg-muted/50 flex items-center justify-center shrink-0">
              <Network className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-mono text-sm truncate">
                {endpoint.host}:{endpoint.port}
              </div>
              <div className="text-xs text-muted-foreground truncate mt-0.5 flex items-center gap-1.5">
                {state === "checking" ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                <span className={STATE_CLASS[state]}>{ADDRESS_STATE_LABEL[state]}</span>
                {h?.latencyMs !== undefined ? <span>{` • ${h.latencyMs} ms`}</span> : null}
              </div>
            </div>
            {selected ? <Check className="h-4 w-4 text-primary shrink-0" /> : null}
          </button>
        );
      })}
    </div>
  );
}
